import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, BellSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public unsubscribe landing — linked from the Manage notifications footer in
 * every digest email. One-click confirms removal of every subscription this
 * email has across the workspace.
 */
const ParentUnsubscribePage = () => {
  const { shareCode } = useParams();
  const [params] = useSearchParams();
  const initialEmail = params.get('email') || '';

  const [email, setEmail] = useState(initialEmail);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // null | { workspace_name, removed }

  // Auto-submit if the email arrived on the URL — one-click flow from email
  useEffect(() => {
    if (initialEmail) {
      submit(initialEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmail]);

  const submit = async (target) => {
    const value = (target || email).trim().toLowerCase();
    if (!value || !value.includes('@')) {
      toast.error('Enter your email');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/share/classroom/${shareCode}/unsubscribe`,
        { email: value },
      );
      setDone({
        workspace_name: res.data?.workspace_name || 'this classroom',
        removed: res.data?.removed || 0,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not unsubscribe');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="parent-unsubscribe-page">
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="flex items-center gap-2 mb-3">
          <BellSlash size={16} weight="fill" className="text-[#C29D52]" />
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C29D52]">
            Manage notifications
          </span>
        </div>

        {done ? (
          <div data-testid="parent-unsubscribe-done">
            <h1 className="text-3xl font-black tracking-tight mb-2">You're unsubscribed.</h1>
            <p className="text-sm text-gray-400 mb-6">
              We've removed your email from <strong className="text-white">{done.workspace_name}</strong>.
              You won't receive any more daily updates.
            </p>
            <div
              className="rounded-xl px-4 py-3 flex items-center gap-2 text-xs"
              style={{
                background: 'rgba(34,197,94,0.08)',
                border: '1px solid rgba(34,197,94,0.25)',
              }}
            >
              <CheckCircle size={14} weight="fill" className="text-emerald-400" />
              <span className="text-emerald-300 font-bold uppercase tracking-[0.14em]">
                Confirmed
              </span>
              <span className="text-gray-400">
                — {done.removed} subscription{done.removed === 1 ? '' : 's'} removed.
              </span>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <h1 className="text-3xl font-black tracking-tight mb-2">Stop daily updates?</h1>
            <p className="text-sm text-gray-400 mb-6">
              Confirm your email and we'll remove every subscription tied to it for this classroom.
              You can resubscribe any time from the share link.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
              className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#B89762]/40 transition-colors mb-3"
              data-testid="parent-unsubscribe-email"
            />
            <button
              type="submit"
              disabled={submitting || !email}
              className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.015] disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="parent-unsubscribe-submit"
            >
              {submitting ? 'Unsubscribing…' : 'Unsubscribe'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ParentUnsubscribePage;
